import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setSessionTimeoutMinutes } from '../../store/slices/settingsSlice';
import { useFeatureFlags } from '../../config/featureFlags';
import { useAdminApiService } from '../../hooks/useAdminApiService';

const AdminSessionSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { auth } = useFeatureFlags();
  const { getSessionSettings, updateSessionSettings, getInactivitySettings, updateInactivitySettings } = useAdminApiService();
  const sessionTimeoutMinutes = useAppSelector(state => state.settings.sessionTimeoutMinutes);
  
  // Local state for form values
  const [values, setValues] = useState({
    adminSessionTimeout: auth.adminSessionTimeoutMinutes,
    retailSessionTimeout: auth.retailSessionTimeoutMinutes,
    jwtExpiryMinutes: auth.jwtExpiryMinutes,
    refreshThresholdMinutes: auth.refreshThresholdMinutes,
    tradingBotRefreshIntervalSeconds: 60,
    inactivityTimeoutEnabled: true,
    countdownSeconds: 120
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Load settings from backend on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      
      // Load session settings
      const sessionResponse = await getSessionSettings();
      if (sessionResponse.success) {
        const sessionData = sessionResponse.data;
        setValues(prev => ({
          ...prev,
          adminSessionTimeout: sessionData.adminSessionTimeoutMinutes || auth.adminSessionTimeoutMinutes,
          retailSessionTimeout: sessionData.retailSessionTimeoutMinutes || auth.retailSessionTimeoutMinutes,
          jwtExpiryMinutes: sessionData.jwtExpiryMinutes || auth.jwtExpiryMinutes,
          refreshThresholdMinutes: sessionData.refreshThresholdMinutes || auth.refreshThresholdMinutes,
          tradingBotRefreshIntervalSeconds: sessionData.tradingBotRefreshIntervalSeconds || 60,
        }));
      }

      // Load inactivity settings
      const inactivityResponse = await getInactivitySettings();
      if (inactivityResponse.success) {
        const inactivityData = inactivityResponse.data;
        setValues(prev => ({
          ...prev,
          inactivityTimeoutEnabled: inactivityData.inactivityTimeoutEnabled !== false,
          countdownSeconds: inactivityData.countdownSeconds || 120,
        }));
      }

    } catch (error) {
      console.error('Failed to load settings:', error);
      setSaveMessage('Failed to load current settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: number | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      // Validate values
      if (values.adminSessionTimeout < 1 || values.adminSessionTimeout > 480) {
        setSaveMessage('Admin session timeout must be between 1 and 480 minutes');
        return;
      }
      if (values.retailSessionTimeout < 1 || values.retailSessionTimeout > 480) {
        setSaveMessage('Retail session timeout must be between 1 and 480 minutes');
        return;
      }
      if (values.jwtExpiryMinutes < 5 || values.jwtExpiryMinutes > 60) {
        setSaveMessage('JWT expiry must be between 5 and 60 minutes');
        return;
      }
      if (values.refreshThresholdMinutes < 1 || values.refreshThresholdMinutes > 30) {
        setSaveMessage('Refresh threshold must be between 1 and 30 minutes');
        return;
      }
      if (values.tradingBotRefreshIntervalSeconds < 60 || values.tradingBotRefreshIntervalSeconds > 600) {
        setSaveMessage('Trading bot refresh interval must be between 60 and 600 seconds');
        return;
      }

      // Save session settings to backend
      const sessionSettings = {
        adminSessionTimeoutMinutes: values.adminSessionTimeout,
        retailSessionTimeoutMinutes: values.retailSessionTimeout,
        jwtExpiryMinutes: values.jwtExpiryMinutes,
        refreshThresholdMinutes: values.refreshThresholdMinutes,
        tradingBotRefreshIntervalSeconds: values.tradingBotRefreshIntervalSeconds,
      };

      const sessionResponse = await updateSessionSettings(sessionSettings);
      if (!sessionResponse.success) {
        throw new Error('Failed to update session settings');
      }

      // Save inactivity settings to backend
      const inactivitySettings = {
        inactivityTimeoutEnabled: values.inactivityTimeoutEnabled,
        countdownSeconds: values.countdownSeconds,
      };

      const inactivityResponse = await updateInactivitySettings(inactivitySettings);
      if (!inactivityResponse.success) {
        throw new Error('Failed to update inactivity settings');
      }

      // Save to Redux store (for immediate effect)
      dispatch(setSessionTimeoutMinutes(values.adminSessionTimeout));

      setSaveMessage('Settings saved successfully! Changes will take effect immediately.');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000);

    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        maxWidth: 600, 
        margin: '24px auto', 
        padding: '24px',
        textAlign: 'center'
      }}>
        <div>Loading settings...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: '32px auto', 
      padding: '32px',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #f3f4f6'
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          color: '#1f2937',
          fontSize: '28px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            ⏱️ Session & Timeout Management
          </span>
        </h2>
        <p style={{ 
          margin: 0, 
          color: '#6b7280', 
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          Configure session timeouts and inactivity settings for both admin and retail users
        </p>
      </div>
      
      {/* Admin Session Settings */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            👨‍💼
          </div>
          <h3 style={{ 
            margin: 0, 
            color: '#1e293b',
            fontSize: '20px',
            fontWeight: '600'
          }}>
            Admin Session Settings
          </h3>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px',
          alignItems: 'start'
        }}>
          <div style={{
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Admin Session Timeout (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={480}
              value={values.adminSessionTimeout}
              onChange={e => handleInputChange('adminSessionTimeout', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                background: '#10b981',
                borderRadius: '50%'
              }}></span>
              Current: {auth.adminSessionTimeoutMinutes} minutes
            </div>
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              JWT Token Expiry (minutes)
            </label>
            <input
              type="number"
              min={5}
              max={60}
              value={values.jwtExpiryMinutes}
              onChange={e => handleInputChange('jwtExpiryMinutes', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                background: '#10b981',
                borderRadius: '50%'
              }}></span>
              Current: {auth.jwtExpiryMinutes} minutes
            </div>
          </div>
        </div>
      </div>

      {/* Retail Session Settings */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderRadius: '12px',
        border: '1px solid #d1fae5'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            🛒
          </div>
          <h3 style={{ 
            margin: 0, 
            color: '#1e293b',
            fontSize: '20px',
            fontWeight: '600'
          }}>
            Retail Session Settings
          </h3>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px',
          alignItems: 'start'
        }}>
          <div style={{
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Retail Session Timeout (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={480}
              value={values.retailSessionTimeout}
              onChange={e => handleInputChange('retailSessionTimeout', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#10b981';
                e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                background: '#10b981',
                borderRadius: '50%'
              }}></span>
              Current: {auth.retailSessionTimeoutMinutes} minutes
            </div>
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Refresh Threshold (minutes)
            </label>
        <input
          type="number"
          min={1}
              max={30}
              value={values.refreshThresholdMinutes}
              onChange={e => handleInputChange('refreshThresholdMinutes', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#10b981';
                e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                background: '#10b981',
                borderRadius: '50%'
              }}></span>
              Current: {auth.refreshThresholdMinutes} minutes
            </div>
          </div>
        </div>
      </div>

      {/* Inactivity Timeout Settings */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '12px',
        border: '1px solid #fbbf24'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            ⚠️
          </div>
          <h3 style={{ 
            margin: 0, 
            color: '#1e293b',
            fontSize: '20px',
            fontWeight: '600'
          }}>
            Inactivity Timeout Settings
          </h3>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px',
          alignItems: 'start'
        }}>
          <div style={{
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: '12px'
            }}>
              <input
                type="checkbox"
                checked={values.inactivityTimeoutEnabled}
                onChange={e => handleInputChange('inactivityTimeoutEnabled', e.target.checked)}
                style={{ 
                  marginRight: '12px',
                  width: '18px',
                  height: '18px',
                  accentColor: '#f59e0b'
                }}
              />
              <label style={{ 
                fontWeight: '600',
                color: '#374151',
                fontSize: '14px',
                margin: 0,
                cursor: 'pointer'
              }}>
                Enable Inactivity Timeout
              </label>
            </div>
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280',
              marginLeft: '30px'
            }}>
              Shows warning modal when users are inactive
            </div>
          </div>
          
          <div style={{
            background: '#ffffff',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '12px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Warning Countdown (seconds)
      </label>
            <input
              type="number"
              min={30}
              max={300}
              value={values.countdownSeconds}
              onChange={e => handleInputChange('countdownSeconds', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#f59e0b';
                e.target.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '8px'
            }}>
              Time users have to respond to warning
            </div>
          </div>
        </div>
      </div>

      {/* Trading Bot Settings */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #ecfeff 100%)',
        borderRadius: '12px',
        border: '1px solid #e0f2fe'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '20px',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            🤖
          </div>
          <div>
            <h3 style={{ 
              margin: 0, 
              color: '#1e293b',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              Trading Bot Settings
            </h3>
            <p style={{ 
              margin: '4px 0 0 0', 
              color: '#64748b', 
              fontSize: '14px'
            }}>
              Control how often the strategy bots scan the market and execute logic.
            </p>
          </div>
        </div>

        <div style={{
          background: '#ffffff',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '12px', 
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px'
          }}>
            Strategy Bot Refresh Interval (seconds)
          </label>
          <input
            type="number"
            min={60}
            max={600}
            step={10}
            value={values.tradingBotRefreshIntervalSeconds}
            onChange={(e) => handleInputChange('tradingBotRefreshIntervalSeconds', Number(e.target.value))}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          <p style={{ 
            marginTop: '8px', 
            marginBottom: 0,
            color: '#6b7280', 
            fontSize: '13px',
            lineHeight: 1.5
          }}>
            Controls how often the meme coin pairs strategy engine runs. 
            <strong> Default:</strong> 60 seconds. 
            <strong> Range:</strong> 60–600 seconds. 
            Increasing this value will make the bots check the market less frequently (fewer trades, lower CPU/API usage).
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '32px',
        padding: '24px',
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '16px 32px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            if (!isSaving) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
        >
          {isSaving ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Saving...
            </>
          ) : (
            <>
              💾 Save Settings
            </>
          )}
      </button>
        
        {saveMessage && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: saveMessage.includes('successfully') ? '#065f46' : '#991b1b',
            background: saveMessage.includes('successfully') ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${saveMessage.includes('successfully') ? '#a7f3d0' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            {saveMessage.includes('successfully') ? '✅' : '❌'} {saveMessage}
          </div>
        )}
      </div>

      {/* Current Status */}
      <div style={{ 
        marginTop: '32px', 
        padding: '24px', 
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h4 style={{ 
          margin: '0 0 16px 0', 
          color: '#1e293b',
          fontSize: '18px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📊 Current Configuration
        </h4>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px', 
          fontSize: '14px' 
        }}>
          <div style={{
            background: '#ffffff',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: '500', color: '#374151' }}>Admin Timeout:</span>
            <span style={{ fontWeight: '600', color: '#1e293b' }}>{auth.adminSessionTimeoutMinutes} minutes</span>
          </div>
          <div style={{
            background: '#ffffff',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: '500', color: '#374151' }}>Retail Timeout:</span>
            <span style={{ fontWeight: '600', color: '#1e293b' }}>{auth.retailSessionTimeoutMinutes} minutes</span>
          </div>
          <div style={{
            background: '#ffffff',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: '500', color: '#374151' }}>JWT Expiry:</span>
            <span style={{ fontWeight: '600', color: '#1e293b' }}>{auth.jwtExpiryMinutes} minutes</span>
          </div>
          <div style={{
            background: '#ffffff',
            padding: '12px 16px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: '500', color: '#374151' }}>Refresh Threshold:</span>
            <span style={{ fontWeight: '600', color: '#1e293b' }}>{auth.refreshThresholdMinutes} minutes</span>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminSessionSettings; 